import AgencyOnboardingModalShell, { AGENCY_ONBOARDING_MODAL_NAME } from '@root/joinroot.com/src/components/agents/agency-onboarding-modal/agency-onboarding-modal-shell';
import Button, { BUTTON_VARIANT_SECONDARY } from '@root/core/src/components/button';
import Input, { TextTypes } from '@root/core/src/components/input';
import PropTypes from '@root/vendor/prop-types';
import React, { useState } from '@root/vendor/react';
import Responsive from '@root/core/src/utils/responsive';
import Select from '@root/core/src/components/select/select';
import useForm from '@root/core/src/hooks/use-form';
import { Colors, StyleSheet, Theme } from '@root/core/src/utils/styles';
import {
  isFormatted,
  isRequired,
  isValidEmail,
  isValidPhoneNumber,
} from '@root/core/src/utils/validators';

const STEP_KEYS = {
  AGENT: 'agent',
  AGENCY: 'agency',
  BUSINESS: 'business',
};

const STEP_ORDER = [STEP_KEYS.AGENT, STEP_KEYS.AGENCY, STEP_KEYS.BUSINESS];

const STEP_LABELS = {
  [STEP_KEYS.AGENT]: 'Agent',
  [STEP_KEYS.AGENCY]: 'Agency',
  [STEP_KEYS.BUSINESS]: 'Business',
};

const STEP_HEADINGS = {
  [STEP_KEYS.AGENT]: 'First, let\'s start with your Principal Agent information.',
  [STEP_KEYS.AGENCY]: 'Next, tell us about your agency.',
  [STEP_KEYS.BUSINESS]: 'A few more details and we\'ll get your application on its way.',
};

const STEP_FIELDS = {
  [STEP_KEYS.AGENT]: ['legalName', 'principalAgentNpn', 'emailAddress', 'phoneNumber'],
  [STEP_KEYS.AGENCY]: ['agencyName', 'agencyType', 'agencyWebsite', 'agencyNpn', 'agencyAddress', 'additionalProducers'],
  [STEP_KEYS.BUSINESS]: ['totalWrittenPremium', 'personalLinesAutoPercentage', 'yearsInBusiness', 'comparativeRater', 'heardAboutRoot', 'topCarriers'],
};

const AGENCY_TYPE_OPTIONS = [
  {
    label: 'Independent Agency',
    value: 'independent-agency',
  },
  {
    label: 'Independent Sole Proprietor',
    value: 'independent-sole-proprietor',
  },
  {
    label: 'Aggregator',
    value: 'aggregator',
  },
  {
    label: 'Market Access Provider',
    value: 'market-access-provider',
  },
  {
    label: 'Wholesaler',
    value: 'wholesaler',
  },
];

const COMPARATIVE_RATER_OPTIONS = [
  {
    label: 'EZLynx',
    value: 'ezlynx',
  },
  {
    label: 'PL Rating',
    value: 'pl-rating',
  },
  {
    label: 'QuinStreet',
    value: 'quinstreet',
  },
  {
    label: 'Agency\'s internal rater',
    value: 'agencys-internal-rater',
  },
  {
    label: 'Other',
    value: 'other',
  },
  {
    label: 'My agency does not use a comparative rater',
    value: 'no-comparative-rater',
  },
];

const HEARD_ABOUT_ROOT_OPTIONS = [
  {
    label: 'Online Advertisement',
    value: '91',
  },
  {
    label: 'EZLynx',
    value: '92',
  },
  {
    label: 'TrustedChoice',
    value: '93',
  },
  {
    label: 'TurboRater',
    value: '94',
  },
  {
    label: 'PL Rating',
    value: '95',
  },
  {
    label: 'Email from Root',
    value: '96',
  },
  {
    label: 'Phone Call from Root',
    value: '97',
  },
  {
    label: 'Physical Mail Advertisement',
    value: '107',
  },
  {
    label: 'Friend/Colleague',
    value: '98',
  },
  {
    label: 'American Agents Alliance Conference & Expo',
    value: '111',
  },
  {
    label: 'Other',
    value: '99',
  },
];

const REQUIRED_FIELD_ERROR = 'Required field';
const NUMBERS_ONLY_ERROR = 'Please enter numbers only';
const WHOLE_NUMBER_ERROR = 'Please enter a whole number';
const VALID_EMAIL_ERROR = 'Please enter a valid email address';
const VALID_PHONE_ERROR = 'Please enter a valid phone number';
const VALID_WEBSITE_ERROR = 'Please enter a valid website';
const VALID_AMOUNT_ERROR = 'Please enter a valid amount';
const VALID_PERCENTAGE_ERROR = 'Enter a value between 0 and 100';

const DIGITS_ONLY_REGEX = /^\d+$/;
const REQUIRED_FIELDS = [...new Set(Object.values(STEP_FIELDS).flat())];

const trimWhitespace = (value = '') => value.trim();
const trimLeadingWhitespace = (value = '') => value.replace(/^\s+/, '');
const digitsOnly = (value = '') => value.toString().replace(/\D/g, '');

const isValidWebsiteField = (fieldName) => (values, errors) => {
  const value = values[fieldName];
  if (!value) {
    return;
  }

  const website = value.trim();

  if (!website) {
    errors[fieldName] = VALID_WEBSITE_ERROR;
    return;
  }

  try {
    const withProtocol = /^https?:\/\//i.test(website) ? website : `https://${website}`;
    const parsed = new URL(withProtocol);
    if (!parsed.hostname || !parsed.hostname.includes('.')) {
      errors[fieldName] = VALID_WEBSITE_ERROR;
    }
  } catch {
    errors[fieldName] = VALID_WEBSITE_ERROR;
  }
};

const hasAtLeastOneDigit = (fieldName, errorMessage) => (values, errors) => {
  const value = values[fieldName];
  if (value && !/\d/.test(value)) {
    errors[fieldName] = errorMessage;
  }
};

const isPercentageBetweenZeroAndHundred = (fieldName) => (values, errors) => {
  const value = values[fieldName];
  if (!value) {
    return;
  }

  const percentage = Number(value.toString().replace('%', '').trim());
  if (Number.isNaN(percentage) || percentage < 0 || percentage > 100) {
    errors[fieldName] = VALID_PERCENTAGE_ERROR;
  }
};

const validations = [
  ...REQUIRED_FIELDS.map((fieldName) => isRequired(fieldName, REQUIRED_FIELD_ERROR)),
  isValidEmail('emailAddress', VALID_EMAIL_ERROR),
  isValidPhoneNumber('phoneNumber', VALID_PHONE_ERROR),
  isFormatted('principalAgentNpn', DIGITS_ONLY_REGEX, NUMBERS_ONLY_ERROR),
  isValidWebsiteField('agencyWebsite'),
  isFormatted('agencyNpn', DIGITS_ONLY_REGEX, NUMBERS_ONLY_ERROR),
  isFormatted('additionalProducers', DIGITS_ONLY_REGEX, WHOLE_NUMBER_ERROR),
  hasAtLeastOneDigit('totalWrittenPremium', VALID_AMOUNT_ERROR),
  isPercentageBetweenZeroAndHundred('personalLinesAutoPercentage'),
  isFormatted('yearsInBusiness', DIGITS_ONLY_REGEX, WHOLE_NUMBER_ERROR),
];

export default function AgencyOnboardingModal({
  anchor,
  isShowing,
  sectionSpacing = {},
  subtitle,
  title,
  triggerLabel = 'Get appointed',
}) {
  const [isUncontrolledShowing, setIsUncontrolledShowing] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [submission, setSubmission] = useState(null);

  const form = useForm({
    initialValues: {
      legalName: '',
      principalAgentNpn: '',
      emailAddress: '',
      phoneNumber: '',
      agencyName: '',
      agencyType: '',
      agencyWebsite: '',
      agencyNpn: '',
      agencyAddress: '',
      additionalProducers: '',
      totalWrittenPremium: '',
      personalLinesAutoPercentage: '',
      yearsInBusiness: '',
      comparativeRater: '',
      heardAboutRoot: '',
      topCarriers: '',
    },
    validations,
    validateOnBlur: false,
  });

  const isControlled = typeof isShowing === 'boolean';
  const shouldShowModal = isControlled ? isShowing : isUncontrolledShowing;
  const currentStepKey = STEP_ORDER[currentStepIndex];
  const isComplete = currentStepKey === undefined;

  const openModal = () => {
    if (!isControlled) {
      setIsUncontrolledShowing(true);
    }
  };

  const closeModal = () => {
    if (!isControlled) {
      setIsUncontrolledShowing(false);
    }
  };

  const onComplete = (values) => {
    setSubmission({
      values,
      meta: {
        completedAt: new Date().toISOString(),
        source: AGENCY_ONBOARDING_MODAL_NAME,
        stepsCompleted: STEP_ORDER.length,
      },
    });
    setCurrentStepIndex(STEP_ORDER.length);
  };

  const handleBack = () => {
    setCurrentStepIndex((previousIndex) => Math.max(previousIndex - 1, 0));
  };

  const handleContinue = (event) => {
    event.preventDefault();

    const stepErrors = form.validate();

    const firstFieldWithError = STEP_FIELDS[currentStepKey].find((fieldName) => !!stepErrors[fieldName]);
    if (firstFieldWithError) {
      form.setFieldError(firstFieldWithError, stepErrors[firstFieldWithError]);
      return;
    }

    if (currentStepIndex === STEP_ORDER.length - 1) {
      onComplete(form.values);
      return;
    }

    setCurrentStepIndex(currentStepIndex + 1);
  };

  const sectionSpacingStyles = {
    paddingTop: (sectionSpacing.top || 0) / 1.25,
    paddingBottom: (sectionSpacing.bottom || 0) / 1.25,
    ...Responsive.md({
      paddingTop: sectionSpacing.top || 0,
      paddingBottom: sectionSpacing.bottom || 0,
    }),
  };

  const renderStepIndicators = () => (
    <div css={styles.stepIndicators}>
      {STEP_ORDER.map((stepKey, index) => {
        const isActive = currentStepIndex === index;
        const isCompleted = currentStepIndex > index;

        return (
          <React.Fragment key={stepKey}>
            <div css={styles.stepItem}>
              <div css={[styles.stepCircle, isActive && styles.stepCircleActive, isCompleted && styles.stepCircleComplete]}>
                {index + 1}
              </div>
              <span css={[styles.stepLabel, isActive && styles.stepLabelActive]}>{STEP_LABELS[stepKey]}</span>
            </div>
            {index < STEP_ORDER.length - 1 && <div css={styles.stepDivider} />}
          </React.Fragment>
        );
      })}
    </div>
  );

  const renderStepBody = () => {
    if (currentStepKey === STEP_KEYS.AGENT) {
      const legalNameField = form.getFieldProps('legalName', { sanitize: trimLeadingWhitespace });
      const principalAgentNpnField = form.getFieldProps('principalAgentNpn', { sanitize: digitsOnly });
      const emailAddressField = form.getFieldProps('emailAddress', { sanitize: trimWhitespace });
      const phoneNumberField = form.getFieldProps('phoneNumber', { sanitize: digitsOnly });

      return (
        <>
          <Input
            label={'Legal name (first & last)'}
            wrapperStyle={styles.inputWrapper}
            {...legalNameField}
          />
          <Input
            label={'National Producer Number'}
            wrapperStyle={styles.inputWrapper}
            {...principalAgentNpnField}
          />
          <Input
            inputType={TextTypes.EMAIL}
            label={'Email address'}
            wrapperStyle={styles.inputWrapper}
            {...emailAddressField}
          />
          <Input
            inputType={TextTypes.TEL}
            label={'Phone number'}
            wrapperStyle={styles.inputWrapper}
            {...phoneNumberField}
          />
        </>
      );
    }

    if (currentStepKey === STEP_KEYS.AGENCY) {
      const agencyTypeField = form.getFieldProps('agencyType');
      const agencyNameField = form.getFieldProps('agencyName', { sanitize: trimLeadingWhitespace });
      const agencyWebsiteField = form.getFieldProps('agencyWebsite', { sanitize: trimWhitespace });
      const agencyNpnField = form.getFieldProps('agencyNpn', { sanitize: digitsOnly });
      const agencyAddressField = form.getFieldProps('agencyAddress', { sanitize: trimLeadingWhitespace });
      const additionalProducersField = form.getFieldProps('additionalProducers', { sanitize: digitsOnly });

      return (
        <>
          <Input
            label={'Agency name'}
            wrapperStyle={styles.inputWrapper}
            {...agencyNameField}
          />
          <div css={styles.selectWrapper}>
            <Select
              errorLabel={agencyTypeField.errorLabel}
              inputId={'agencyType'}
              inputName={'agencyType'}
              onChange={agencyTypeField.onChange}
              options={AGENCY_TYPE_OPTIONS}
              placeholder={'Agency Type'}
              selectedValue={agencyTypeField.value}
            />
          </div>
          <Input
            label={'Agency website'}
            wrapperStyle={styles.inputWrapper}
            {...agencyWebsiteField}
          />
          <Input
            label={'Agency National Producer Number'}
            wrapperStyle={styles.inputWrapper}
            {...agencyNpnField}
          />
          <Input
            label={'Agency address'}
            wrapperStyle={styles.inputWrapper}
            {...agencyAddressField}
          />
          <Input
            label={'How many additional producers would you like to be appointed with Root?'}
            wrapperStyle={styles.inputWrapper}
            {...additionalProducersField}
          />
        </>
      );
    }

    const comparativeRaterField = form.getFieldProps('comparativeRater');
    const heardAboutRootField = form.getFieldProps('heardAboutRoot');
    const totalWrittenPremiumField = form.getFieldProps('totalWrittenPremium', { sanitize: trimLeadingWhitespace });
    const personalLinesAutoPercentageField = form.getFieldProps('personalLinesAutoPercentage', { sanitize: trimWhitespace });
    const yearsInBusinessField = form.getFieldProps('yearsInBusiness', { sanitize: digitsOnly });
    const topCarriersField = form.getFieldProps('topCarriers', { sanitize: trimLeadingWhitespace });

    return (
      <>
        <Input
          label={'What is your agency\'s total written premium?'}
          wrapperStyle={styles.inputWrapper}
          {...totalWrittenPremiumField}
        />
        <Input
          label={'What percentage is personal lines auto?'}
          wrapperStyle={styles.inputWrapper}
          {...personalLinesAutoPercentageField}
        />
        <Input
          label={'How many years has your agency been in business?'}
          wrapperStyle={styles.inputWrapper}
          {...yearsInBusinessField}
        />
        <div css={styles.selectWrapper}>
          <Select
            errorLabel={comparativeRaterField.errorLabel}
            inputId={'comparativeRater'}
            inputName={'comparativeRater'}
            onChange={comparativeRaterField.onChange}
            options={COMPARATIVE_RATER_OPTIONS}
            placeholder={'Does your agency use a comparative rater? If so, which one?'}
            selectedValue={comparativeRaterField.value}
          />
        </div>
        <div css={styles.selectWrapper}>
          <Select
            errorLabel={heardAboutRootField.errorLabel}
            inputId={'heardAboutRoot'}
            inputName={'heardAboutRoot'}
            onChange={heardAboutRootField.onChange}
            options={HEARD_ABOUT_ROOT_OPTIONS}
            placeholder={'How did you hear about Root?'}
            selectedValue={heardAboutRootField.value}
          />
        </div>
        <Input
          label={'Please provide the top three carriers your agency writes the most auto insurance with'}
          wrapperStyle={styles.inputWrapper}
          {...topCarriersField}
        />
      </>
    );
  };

  const renderFooter = () => {
    if (isComplete) {
      return (
        <div css={styles.completeFooter}>
          <Button
            onClick={closeModal}
            type={'button'}
          >
            {'Close'}
          </Button>
        </div>
      );
    }

    return (
      <>
        <div>
          {currentStepIndex > 0 &&
            <Button
              onClick={handleBack}
              type={'button'}
              variant={BUTTON_VARIANT_SECONDARY}
            >
              {'Back'}
            </Button>
          }
        </div>
        <Button
          onClick={handleContinue}
          type={'button'}
        >
          {'Continue'}
        </Button>
      </>
    );
  };

  const renderBody = () => {
    if (isComplete) {
      const submittedEmail = submission?.values?.emailAddress || form.values.emailAddress;

      return (
        <div css={styles.completeBody}>
          <p css={styles.rootWordmark}>{'Root'}</p>
          <h3 css={styles.completeHeading}>{'Application submitted.'}</h3>
          <p css={styles.completeCopy}>{'Thank you for your interest in partnering with Root. Our team will review your application and contact you soon.'}</p>
          <div css={styles.nextStepsCard}>
            <p css={styles.nextStepsTitle}>{'What happens next?'}</p>
            <ul css={styles.nextStepsList}>
              <li>{'Our team reviews your application'}</li>
              <li>{`You'll receive a confirmation email at ${submittedEmail || 'your email address'}`}</li>
              <li>{'A representative will call you to discuss next steps'}</li>
            </ul>
          </div>
        </div>
      );
    }

    return (
      <form
        css={styles.form}
        onSubmit={handleContinue}
      >
        {renderStepIndicators()}
        <h3 css={styles.stepHeading}>{STEP_HEADINGS[currentStepKey]}</h3>
        <div css={styles.fields}>{renderStepBody()}</div>
      </form>
    );
  };

  return (
    <section
      css={[styles.section, sectionSpacingStyles]}
      data-testid={'agency-onboarding-modal'}
      id={anchor}
    >
      {triggerLabel &&
        <Button
          css={styles.triggerButton}
          data-testid={'agency-onboarding-modal-trigger'}
          onClick={openModal}
          type={'button'}
        >
          {triggerLabel}
        </Button>
      }
      <AgencyOnboardingModalShell
        footer={renderFooter()}
        isShowing={shouldShowModal}
        onClose={closeModal}
        subtitle={subtitle}
        title={title}
      >
        {renderBody()}
      </AgencyOnboardingModalShell>
    </section>
  );
}

AgencyOnboardingModal.propTypes = {
  anchor: PropTypes.string,
  isShowing: PropTypes.bool,
  sectionSpacing: PropTypes.shape({
    bottom: PropTypes.number,
    top: PropTypes.number,
  }),
  subtitle: PropTypes.string,
  title: PropTypes.string,
  triggerLabel: PropTypes.string,
};

const styles = StyleSheet.create({
  section: {
    width: '100%',
  },
  triggerButton: {
    width: 'min(216px, 100%)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
  },
  stepIndicators: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    maxWidth: 620,
    margin: '0 auto',
    marginBottom: 4,
  },
  stepItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: '0 0 auto',
  },
  stepCircle: {
    ...Theme.paragraph2({
      bold: true,
    }),
    color: Colors.gray50(),
    width: 18,
    height: 18,
    borderRadius: 18,
    border: `1px solid ${Colors.gray40()}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  stepCircleActive: {
    borderColor: Colors.rootOrange(),
    color: Colors.rootOrange(),
  },
  stepCircleComplete: {
    borderColor: Colors.gray50(),
    color: Colors.gray50(),
  },
  stepLabel: {
    ...Theme.paragraph2(),
    color: Colors.gray50(),
  },
  stepLabelActive: {
    ...Theme.paragraph2({
      bold: true,
    }),
    color: Colors.nearBlack(),
  },
  stepDivider: {
    flex: '1 1 0%',
    height: 1,
    backgroundColor: Colors.gray30(),
    margin: '0 12px',
  },
  stepHeading: {
    ...Theme.heading5(),
    margin: 0,
  },
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  inputWrapper: {
    marginTop: 0,
  },
  selectWrapper: {
    '& > div': {
      width: '100%',
    },
  },
  completeBody: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: 14,
    paddingTop: 6,
  },
  rootWordmark: {
    ...Theme.displayHeading3(),
    color: Colors.rootOrange(),
    margin: 0,
  },
  completeHeading: {
    ...Theme.heading1(),
    margin: 0,
  },
  completeCopy: {
    ...Theme.paragraph1(),
    maxWidth: 540,
    margin: 0,
  },
  nextStepsCard: {
    marginTop: 4,
    width: '100%',
    maxWidth: 420,
    border: `1px solid ${Colors.gray20()}`,
    borderRadius: 10,
    backgroundColor: Colors.gray10(),
    padding: '18px 18px 14px',
  },
  nextStepsTitle: {
    ...Theme.paragraph1({
      bold: true,
    }),
    color: Colors.nearBlack(),
    marginTop: 0,
    marginBottom: 10,
  },
  nextStepsList: {
    ...Theme.paragraph2(),
    color: Colors.nearBlack(),
    margin: 0,
    paddingLeft: 20,
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  completeFooter: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
});
